import type { ResearchTaskReceipts } from "@/admin/monitoring/company-run-research-data"
import {
  AttributionBadge,
  EvidenceSection,
  Fact,
  Facts,
} from "@/admin/monitoring/evidence-parts"
import {
  elapsedTime,
  formatRunStart,
  formatSearchGeography,
  humanizeKey,
} from "@/admin/monitoring/run-presentation"
import { SourceArtifactViewer } from "@/admin/monitoring/source-artifact-viewer"
import { StoredDetails, StoredValue } from "@/admin/monitoring/stored-value"
import { dataByExecutionOutcome } from "@/constants/terminal/execution-outcome"
import { dataBySearchSubjectKind } from "@/constants/terminal/search-subject-kind"
import { dataBySourceCheckOutcome } from "@/constants/terminal/source-check-outcome"
import { sourcePlatformDisplayName } from "@/terminal/research/platform-display-names"
import {
  loadSearchGeography,
  StoredSearchGeographySchema,
} from "@/terminal/research/search-geography-storage"

const kilobyteFormat = new Intl.NumberFormat("en-US", {
  style: "unit",
  unit: "kilobyte",
  maximumFractionDigits: 1,
})

export const ResearchTaskReceiptChain = ({
  receipts,
}: {
  receipts: ResearchTaskReceipts
}) => {
  const { sourceCheck } = receipts
  if (sourceCheck === null)
    return (
      <p className="text-muted-foreground text-sm">
        No Source Check has run for this Research Task yet.
      </p>
    )
  const geography = StoredSearchGeographySchema.safeParse(
    sourceCheck.searchGeography
  )
  return (
    <div className="space-y-8">
      <EvidenceSection title="Source Records">
        {receipts.sourceRecordCount === 0 ? (
          <p className="text-muted-foreground text-xs">
            {receipts.isAttributionFrozen
              ? "The Assessment attributed none of this Research Task’s Source Records."
              : "None retained."}
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">
            {receipts.sourceRecords.length} of {receipts.sourceRecordCount}{" "}
            shown, unresolved first.
            {receipts.isAttributionFrozen &&
              " These are the Source Records the Assessment attributed, from any Source Check of this Research Task."}
          </p>
        )}
        {receipts.sourceRecords.map((record) => (
          <article
            key={record.sourceRecordId}
            className="space-y-2 border-l-2 pl-3"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <AttributionBadge attribution={record.attribution} />
              <span>{humanizeKey(record.recordKind)}</span>
              <span className="text-muted-foreground break-all">
                {record.sourceRecordKey}
              </span>
            </div>
            {record.sourceCheckId !== sourceCheck.sourceCheckId && (
              <p className="text-muted-foreground text-xs">
                From an earlier Source Check
              </p>
            )}
            <Facts>
              {record.matchRuleKey && (
                <Fact label="Match Rule" value={record.matchRuleKey} />
              )}
              {record.matchedParty && (
                <>
                  <Fact
                    label="Matched party"
                    value={record.matchedParty.name}
                  />
                  <Fact label="Party role" value={record.matchedParty.role} />
                  {record.matchedParty.source_role && (
                    <Fact
                      label="Source role"
                      value={record.matchedParty.source_role}
                    />
                  )}
                </>
              )}
            </Facts>
            {record.matchExplanation && (
              <div className="text-xs">
                <StoredValue value={record.matchExplanation} />
              </div>
            )}
            <StoredDetails
              summary="Source-recorded subject and roles"
              value={record.recordedSubject}
            />
            <StoredDetails
              summary="Stored Source Record"
              value={record.attributes}
            />
          </article>
        ))}
      </EvidenceSection>

      <EvidenceSection title="Source Check">
        <Facts>
          <Fact
            label="Source"
            value={
              <>
                {sourcePlatformDisplayName(sourceCheck.platformKey)}
                <span className="text-muted-foreground block">
                  {humanizeKey(sourceCheck.sourceKey)}
                </span>
              </>
            }
          />
          <Fact
            label="Search subject"
            value={`${dataBySearchSubjectKind[sourceCheck.searchSubjectKind].label} ${sourceCheck.searchSubjectId}`}
          />
          <Fact
            label="Geography"
            value={
              geography.success ? (
                formatSearchGeography(loadSearchGeography(geography.data))
              ) : (
                <StoredValue value={sourceCheck.searchGeography} />
              )
            }
          />
          <Fact
            label="Outcome"
            value={
              sourceCheck.outcome
                ? dataBySourceCheckOutcome[sourceCheck.outcome].label
                : "Pending"
            }
          />
          {sourceCheck.error !== null && (
            <Fact
              label="Error"
              value={<StoredValue value={sourceCheck.error} />}
            />
          )}
          <Fact
            label="Implementation"
            value={`${sourceCheck.implementationKey}@${sourceCheck.implementationVersion}`}
          />
          <Fact
            label="Research policy"
            value={sourceCheck.researchPolicyVersion}
          />
          <Fact
            label="Ran"
            value={`${formatRunStart(sourceCheck.startedAt)}, ${
              sourceCheck.completedAt
                ? `took ${elapsedTime(sourceCheck.startedAt, sourceCheck.completedAt)}`
                : "still running"
            }`}
          />
        </Facts>
        {receipts.otherSourceCheckCount > 0 && (
          <p className="text-muted-foreground text-xs">
            {receipts.otherSourceCheckCount} other Source Checks for this
            Research Task aren’t shown.
          </p>
        )}
      </EvidenceSection>

      <EvidenceSection title="Source Artifacts">
        {receipts.sourceArtifacts.length === 0 && (
          <p className="text-muted-foreground text-xs">None preserved.</p>
        )}
        {receipts.sourceArtifacts.map((artifact) => (
          <div key={artifact.sourceArtifactId} className="border-l-2 pl-3">
            <Facts>
              <Fact label="Role" value={humanizeKey(artifact.role)} />
              <Fact
                label="Retrieved from"
                value={
                  <span className="break-all">{artifact.retrievalLocator}</span>
                }
              />
              <Fact
                label="Content"
                value={`${artifact.mimeType}, ${kilobyteFormat.format(artifact.byteCount / 1000)}`}
              />
              <Fact
                label="SHA-256"
                value={`${artifact.contentHash.slice(0, 12)}…`}
              />
              <Fact
                label="Captured"
                value={formatRunStart(artifact.capturedAt)}
              />
            </Facts>
            <SourceArtifactViewer
              artifact={{
                privateObjectPath: artifact.privateObjectPath,
                label: humanizeKey(artifact.role),
                mimeType: artifact.mimeType,
                byteCount: artifact.byteCount,
              }}
              triggerLabel="View stored content"
            />
          </div>
        ))}
      </EvidenceSection>

      <EvidenceSection title="Source Record Extraction">
        {receipts.sourceRecordExtractions.length === 0 && (
          <p className="text-muted-foreground text-xs">None run.</p>
        )}
        {receipts.sourceRecordExtractions.map((extraction) => (
          <div
            key={extraction.sourceRecordExtractionId}
            className="border-l-2 pl-3"
          >
            <Facts>
              <Fact
                label="Extractor"
                value={`${extraction.extractorKey}@${extraction.extractorVersion}`}
              />
              <Fact
                label="Outcome"
                value={
                  extraction.outcome
                    ? dataByExecutionOutcome[extraction.outcome].label
                    : "Pending"
                }
              />
              {extraction.error !== null && (
                <Fact
                  label="Error"
                  value={<StoredValue value={extraction.error} />}
                />
              )}
            </Facts>
          </div>
        ))}
      </EvidenceSection>
    </div>
  )
}
